// kernel/useViewports.ts
import { useCallback, useRef } from 'react';
import type {
  KernelEntityRuntimeState,
  KernelEntityType,
  KernelHandle,
  KernelViewportBounds,
  KernelViewportRegistration,
  KernelViewportRuntimeState,
  useViewportsReturnType,
} from '#types/types';

interface UseViewportsProps {
  kernel: React.RefObject<KernelHandle | null>;
}

type RuntimeRecord = Record<string, unknown>;

type RegisteredKernelViewport = KernelViewportRegistration & {
  normalizedKey: string;
  renderedViewport?: KernelViewportRuntimeState;
};

interface ViewportAnimationState {
  frameId: number | null;
  startedAt: number;
  durationMs: number;
  startViewport: KernelViewportRuntimeState;
  targetViewport: KernelViewportRuntimeState;
}

interface ViewportDragState {
  viewportKey: string;
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startPanOffset: { x: number; y: number };
}

const DEFAULT_KERNEL_VIEWPORT: KernelViewportRuntimeState = {
  panOffset: { x: 0, y: 0 },
  zoomLevel: 1,
};

const DEFAULT_MIN_ZOOM = 0.004;
const DEFAULT_MAX_ZOOM = 4096;
const DEFAULT_FIT_MARGIN = 1.1;
const BUTTON_ZOOM_FACTOR = 1.6;
const WHEEL_ZOOM_FACTOR = 1.4;

const normalizeViewportKey = (viewportKey: string): string =>
  typeof viewportKey === 'string' ? viewportKey.trim() : '';

export const isRecord = (value: unknown): value is RuntimeRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const normalizeRuntimePath = (path: readonly string[] | undefined): string[] =>
  Array.isArray(path)
    ? path.map((entry) => entry.trim()).filter(Boolean)
    : [];

const clampZoomForRegistration = (registration: RegisteredKernelViewport, zoom: number): number => {
  const minZoom = isFiniteNumber(registration.minZoom) && registration.minZoom > 0
    ? registration.minZoom
    : DEFAULT_MIN_ZOOM;
  const maxZoom = isFiniteNumber(registration.maxZoom) && registration.maxZoom >= minZoom
    ? registration.maxZoom
    : DEFAULT_MAX_ZOOM;
  return Math.min(maxZoom, Math.max(minZoom, zoom));
};

export const parseKernelViewportState = (
  value: unknown,
  fallback: KernelViewportRuntimeState = DEFAULT_KERNEL_VIEWPORT
): KernelViewportRuntimeState | null => {
  if (!isRecord(value)) return null;
  const panOffsetValue = value.panOffset;
  const panOffset = isRecord(panOffsetValue) && isFiniteNumber(panOffsetValue.x) && isFiniteNumber(panOffsetValue.y)
    ? { x: panOffsetValue.x, y: panOffsetValue.y }
    : fallback.panOffset;
  const zoomLevel = isFiniteNumber(value.zoomLevel) && value.zoomLevel > 0
    ? value.zoomLevel
    : fallback.zoomLevel;
  return { panOffset, zoomLevel };
};

const sanitizeViewportForRegistration = (
  registration: RegisteredKernelViewport,
  viewport: KernelViewportRuntimeState | null | undefined
): KernelViewportRuntimeState => {
  const fallback = parseKernelViewportState(registration.defaultViewport) ?? DEFAULT_KERNEL_VIEWPORT;
  const candidate = parseKernelViewportState(viewport) ?? fallback;
  return {
    panOffset: {
      x: isFiniteNumber(candidate.panOffset.x) ? candidate.panOffset.x : fallback.panOffset.x,
      y: isFiniteNumber(candidate.panOffset.y) ? candidate.panOffset.y : fallback.panOffset.y,
    },
    zoomLevel: clampZoomForRegistration(registration, candidate.zoomLevel),
  };
};

export const readNestedRuntimeValue = (runtime: RuntimeRecord, path: readonly string[]): unknown => {
  let cursor: unknown = runtime;
  for (const segment of path) {
    if (!isRecord(cursor)) return undefined;
    cursor = cursor[segment];
  }
  return cursor;
};

const setNestedRuntimeValue = (
  currentValue: unknown,
  path: readonly string[],
  nextValue: unknown
): RuntimeRecord => {
  if (path.length === 0) return {};
  const [head, ...tail] = path;
  if (!head) return {};
  const base = isRecord(currentValue) ? currentValue : {};
  if (tail.length === 0) {
    return { ...base, [head]: nextValue };
  }
  return {
    ...base,
    [head]: setNestedRuntimeValue(base[head], tail, nextValue),
  };
};

const buildRuntimePatchForPath = (
  runtime: RuntimeRecord,
  path: readonly string[],
  nextValue: unknown
): RuntimeRecord => {
  const [head, ...tail] = path;
  if (!head) return {};
  if (tail.length === 0) return { [head]: nextValue };
  return {
    [head]: setNestedRuntimeValue(runtime[head], tail, nextValue),
  };
};

export const isValidBounds = (bounds: KernelViewportBounds | null | undefined): bounds is KernelViewportBounds =>
  Boolean(bounds) &&
  isFiniteNumber(bounds?.x) &&
  isFiniteNumber(bounds?.y) &&
  isFiniteNumber(bounds?.w) &&
  isFiniteNumber(bounds?.h) &&
  bounds.w > 0 &&
  bounds.h > 0;

// Canonical zoom badge label ("x1.5"): the Studio bottom bar and the kernel viewport controls
// both print it, so it lives here rather than in two divergent local copies.
export const formatViewportZoomLabel = (zoomLevel: number): string => {
  if (!Number.isFinite(zoomLevel) || zoomLevel <= 0) return 'x1';
  if (zoomLevel >= 10) return `x${Math.round(zoomLevel)}`;
  if (zoomLevel >= 0.1) return `x${(Math.round(zoomLevel * 10) / 10).toFixed(1)}`;
  return `x${Math.max(0.01, Math.round(zoomLevel * 100) / 100).toFixed(2)}`;
};

const shouldStartBackgroundDrag = (
  registration: RegisteredKernelViewport,
  event: React.PointerEvent<HTMLDivElement>
): boolean => {
  // Middle-button panning should work from interactive objects too (canvas-style pan),
  // while left-button panning keeps respecting the viewport drag mode.
  if (event.button === 1) return true;
  if (registration.dragPanMode === 'anywhere') return true;
  const target = event.target;
  if (!(target instanceof Element)) return event.currentTarget === target;
  return target === event.currentTarget || target.closest('[data-infinite-viewport-background="true"]') !== null;
};

export const useViewports = ({ kernel }: UseViewportsProps): useViewportsReturnType => {
  const registryRef = useRef<Map<string, RegisteredKernelViewport>>(new Map());
  const animationsRef = useRef<Map<string, ViewportAnimationState>>(new Map());
  const dragStateRef = useRef<ViewportDragState | null>(null);

  const resolveRegistration = useCallback((viewportKey: string): RegisteredKernelViewport | null => {
    const normalizedKey = normalizeViewportKey(viewportKey);
    if (!normalizedKey) return null;
    return registryRef.current.get(normalizedKey) ?? null;
  }, []);

  const cancelViewportAnimation = useCallback((viewportKey: string) => {
    const normalizedKey = normalizeViewportKey(viewportKey);
    if (!normalizedKey) return;
    const animation = animationsRef.current.get(normalizedKey);
    if (animation?.frameId !== null && animation?.frameId !== undefined) {
      cancelAnimationFrame(animation.frameId);
    }
    animationsRef.current.delete(normalizedKey);
  }, []);

  const readViewportStateForRegistration = useCallback((registration: RegisteredKernelViewport): KernelViewportRuntimeState => {
    const runtimePath = normalizeRuntimePath(registration.owner.runtimePath);
    if (!kernel.current || runtimePath.length === 0) {
      return sanitizeViewportForRegistration(registration, registration.renderedViewport ?? registration.defaultViewport);
    }
    const runtime = kernel.current.readEntityRuntimeState(registration.owner.entityType, registration.owner.entityId);
    const runtimeViewport = readNestedRuntimeValue(runtime as RuntimeRecord, runtimePath);
    return sanitizeViewportForRegistration(
      registration,
      parseKernelViewportState(runtimeViewport) ?? registration.renderedViewport ?? registration.defaultViewport
    );
  }, [kernel]);

  const applyKernelViewportTransform = useCallback<useViewportsReturnType['applyKernelViewportTransform']>(
    (viewportKey, viewportInput, syncCssVars = true) => {
      const registration = resolveRegistration(viewportKey);
      if (!registration?.transformElement) return;
      const viewport = sanitizeViewportForRegistration(
        registration,
        viewportInput ?? registration.renderedViewport ?? readViewportStateForRegistration(registration)
      );
      const safeZoom = Math.max(0.0001, viewport.zoomLevel);
      registration.transformElement.style.transform = `translate(${viewport.panOffset.x}px, ${viewport.panOffset.y}px) scale(${viewport.zoomLevel})`;
      registration.transformElement.style.transformOrigin = '0 0';
      registration.transformElement.style.setProperty('--zoom-level', String(safeZoom));
      registration.transformElement.style.setProperty('--inverse-zoom-level', String(1 / safeZoom));
      registration.transformElement.style.setProperty('--pan-x', `${viewport.panOffset.x}px`);
      registration.transformElement.style.setProperty('--pan-y', `${viewport.panOffset.y}px`);

      if (registration.containerElement) {
        registration.containerElement.style.setProperty('--viewport-live-pan-x', `${viewport.panOffset.x}px`);
        registration.containerElement.style.setProperty('--viewport-live-pan-y', `${viewport.panOffset.y}px`);
        registration.containerElement.style.setProperty('--viewport-live-zoom', String(safeZoom));
        if (syncCssVars) {
          registration.containerElement.style.setProperty('--zoom-level', String(safeZoom));
          registration.containerElement.style.setProperty('--inverse-zoom-level', String(1 / safeZoom));
        }
      }

      registration.renderedViewport = viewport;
      registryRef.current.set(registration.normalizedKey, registration);
    },
    [readViewportStateForRegistration, resolveRegistration]
  );

  const setViewportStateForRegistration = useCallback((registration: RegisteredKernelViewport, viewportInput: KernelViewportRuntimeState) => {
    const runtimePath = normalizeRuntimePath(registration.owner.runtimePath);
    const viewport = sanitizeViewportForRegistration(registration, viewportInput);
    registration.renderedViewport = viewport;
    registryRef.current.set(registration.normalizedKey, registration);
    applyKernelViewportTransform(registration.normalizedKey, viewport);

    if (!kernel.current || runtimePath.length === 0) return;
    const runtime = kernel.current.readEntityRuntimeState(registration.owner.entityType, registration.owner.entityId);
    const patch = buildRuntimePatchForPath(runtime as RuntimeRecord, runtimePath, viewport);
    kernel.current.patchEntityRuntimeState(
      registration.owner.entityType,
      registration.owner.entityId,
      patch as Partial<KernelEntityRuntimeState<KernelEntityType>>
    );
  }, [applyKernelViewportTransform, kernel]);

  const getKernelViewportState = useCallback<useViewportsReturnType['getKernelViewportState']>((viewportKey) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration) return DEFAULT_KERNEL_VIEWPORT;
    return readViewportStateForRegistration(registration);
  }, [readViewportStateForRegistration, resolveRegistration]);

  const setKernelViewportState = useCallback<useViewportsReturnType['setKernelViewportState']>((viewportKey, viewport) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration) return;
    cancelViewportAnimation(registration.normalizedKey);
    setViewportStateForRegistration(registration, viewport);
  }, [cancelViewportAnimation, resolveRegistration, setViewportStateForRegistration]);

  const animateKernelViewportTo = useCallback((viewportKey: string, targetViewportInput: KernelViewportRuntimeState, durationMs: number) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration) return;
    cancelViewportAnimation(registration.normalizedKey);

    const targetViewport = sanitizeViewportForRegistration(registration, targetViewportInput);
    const startViewport = registration.renderedViewport ?? readViewportStateForRegistration(registration);
    const safeDuration = Math.max(0, durationMs);
    if (safeDuration === 0) {
      setViewportStateForRegistration(registration, targetViewport);
      return;
    }

    const animationState: ViewportAnimationState = {
      frameId: null,
      startedAt: performance.now(),
      durationMs: safeDuration,
      startViewport,
      targetViewport,
    };
    animationsRef.current.set(registration.normalizedKey, animationState);

    const step = () => {
      const activeAnimation = animationsRef.current.get(registration.normalizedKey);
      if (!activeAnimation) return;
      const elapsed = performance.now() - activeAnimation.startedAt;
      const progress = Math.min(1, elapsed / activeAnimation.durationMs);
      const easedProgress = progress;
      const nextViewport: KernelViewportRuntimeState = {
        panOffset: {
          x: activeAnimation.startViewport.panOffset.x + (activeAnimation.targetViewport.panOffset.x - activeAnimation.startViewport.panOffset.x) * easedProgress,
          y: activeAnimation.startViewport.panOffset.y + (activeAnimation.targetViewport.panOffset.y - activeAnimation.startViewport.panOffset.y) * easedProgress,
        },
        zoomLevel: activeAnimation.startViewport.zoomLevel + (activeAnimation.targetViewport.zoomLevel - activeAnimation.startViewport.zoomLevel) * easedProgress,
      };

      applyKernelViewportTransform(registration.normalizedKey, nextViewport, false);

      if (progress < 1) {
        activeAnimation.frameId = requestAnimationFrame(step);
        animationsRef.current.set(registration.normalizedKey, activeAnimation);
        return;
      }

      animationsRef.current.delete(registration.normalizedKey);
      setViewportStateForRegistration(registration, activeAnimation.targetViewport);
    };

    animationState.frameId = requestAnimationFrame(step);
    animationsRef.current.set(registration.normalizedKey, animationState);
  }, [applyKernelViewportTransform, cancelViewportAnimation, readViewportStateForRegistration, resolveRegistration, setViewportStateForRegistration]);

  const registerKernelViewport = useCallback<useViewportsReturnType['registerKernelViewport']>((registrationInput) => {
    const normalizedKey = normalizeViewportKey(registrationInput.key);
    const entityId = typeof registrationInput.owner.entityId === 'string' ? registrationInput.owner.entityId.trim() : '';
    const runtimePath = normalizeRuntimePath(registrationInput.owner.runtimePath);
    if (!normalizedKey || !entityId || runtimePath.length === 0) return;

    const previousRegistration = registryRef.current.get(normalizedKey);
    const registration: RegisteredKernelViewport = {
      ...registrationInput,
      normalizedKey,
      owner: {
        entityType: registrationInput.owner.entityType as KernelEntityType,
        entityId,
        runtimePath,
      },
      contentBounds: isValidBounds(registrationInput.contentBounds) ? registrationInput.contentBounds : null,
      renderedViewport: previousRegistration?.renderedViewport,
    };
    registryRef.current.set(normalizedKey, registration);
    applyKernelViewportTransform(normalizedKey, readViewportStateForRegistration(registration));
  }, [applyKernelViewportTransform, readViewportStateForRegistration]);

  const unregisterKernelViewport = useCallback<useViewportsReturnType['unregisterKernelViewport']>((viewportKey) => {
    const normalizedKey = normalizeViewportKey(viewportKey);
    if (!normalizedKey) return;
    cancelViewportAnimation(normalizedKey);
    registryRef.current.delete(normalizedKey);
    if (dragStateRef.current?.viewportKey === normalizedKey) {
      dragStateRef.current = null;
    }
  }, [cancelViewportAnimation]);

  const zoomViewportAroundPoint = useCallback((viewportKey: string, point: { x: number; y: number }, zoomFactor: number, durationMs: number) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration) return;
    const activeAnimation = animationsRef.current.get(registration.normalizedKey);
    const currentViewport = activeAnimation?.targetViewport ?? registration.renderedViewport ?? readViewportStateForRegistration(registration);
    const currentZoom = Math.max(0.0001, currentViewport.zoomLevel);
    const targetZoom = clampZoomForRegistration(registration, currentZoom * zoomFactor);
    const targetPanOffset = {
      x: point.x - (point.x - currentViewport.panOffset.x) * (targetZoom / currentZoom),
      y: point.y - (point.y - currentViewport.panOffset.y) * (targetZoom / currentZoom),
    };
    animateKernelViewportTo(registration.normalizedKey, { panOffset: targetPanOffset, zoomLevel: targetZoom }, durationMs);
  }, [animateKernelViewportTo, readViewportStateForRegistration, resolveRegistration]);

  const handleKernelViewportWheel = useCallback<useViewportsReturnType['handleKernelViewportWheel']>((viewportKey, event) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration?.containerElement) return;
    event.preventDefault();
    event.stopPropagation();

    if (event.ctrlKey || event.metaKey) {
      const rect = registration.containerElement.getBoundingClientRect();
      const pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const factor = event.deltaY < 0 ? WHEEL_ZOOM_FACTOR : 1 / WHEEL_ZOOM_FACTOR;
      zoomViewportAroundPoint(registration.normalizedKey, pointer, factor, 120);
      return;
    }

    cancelViewportAnimation(registration.normalizedKey);
    const currentViewport = registration.renderedViewport ?? readViewportStateForRegistration(registration);
    setViewportStateForRegistration(registration, {
      zoomLevel: currentViewport.zoomLevel,
      panOffset: {
        x: currentViewport.panOffset.x - event.deltaX,
        y: currentViewport.panOffset.y - event.deltaY,
      },
    });
  }, [cancelViewportAnimation, readViewportStateForRegistration, resolveRegistration, setViewportStateForRegistration, zoomViewportAroundPoint]);

  const handleKernelViewportPointerDown = useCallback<useViewportsReturnType['handleKernelViewportPointerDown']>((viewportKey, event) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration || (event.button !== 0 && event.button !== 1) || !shouldStartBackgroundDrag(registration, event)) return;
    event.preventDefault();
    event.stopPropagation();
    cancelViewportAnimation(registration.normalizedKey);
    const currentViewport = registration.renderedViewport ?? readViewportStateForRegistration(registration);
    dragStateRef.current = {
      viewportKey: registration.normalizedKey,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPanOffset: currentViewport.panOffset,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [cancelViewportAnimation, readViewportStateForRegistration, resolveRegistration]);

  const handleKernelViewportPointerMove = useCallback<useViewportsReturnType['handleKernelViewportPointerMove']>((viewportKey, event) => {
    const normalizedKey = normalizeViewportKey(viewportKey);
    const dragState = dragStateRef.current;
    if (!dragState || dragState.viewportKey !== normalizedKey || dragState.pointerId !== event.pointerId) return;
    const registration = resolveRegistration(normalizedKey);
    if (!registration) return;
    event.preventDefault();
    event.stopPropagation();
    const currentViewport = registration.renderedViewport ?? readViewportStateForRegistration(registration);
    setViewportStateForRegistration(registration, {
      zoomLevel: currentViewport.zoomLevel,
      panOffset: {
        x: dragState.startPanOffset.x + event.clientX - dragState.startClientX,
        y: dragState.startPanOffset.y + event.clientY - dragState.startClientY,
      },
    });
  }, [readViewportStateForRegistration, resolveRegistration, setViewportStateForRegistration]);

  const handleKernelViewportPointerUp = useCallback<useViewportsReturnType['handleKernelViewportPointerUp']>((viewportKey, event) => {
    const normalizedKey = normalizeViewportKey(viewportKey);
    const dragState = dragStateRef.current;
    if (!dragState || dragState.viewportKey !== normalizedKey || dragState.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragStateRef.current = null;
  }, []);

  const fitKernelViewportToRect = useCallback<useViewportsReturnType['fitKernelViewportToRect']>((viewportKey, rectToFit, animationDuration = 240) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration?.containerElement || !isValidBounds(rectToFit)) return;
    const containerWidth = registration.containerElement.offsetWidth;
    const containerHeight = registration.containerElement.offsetHeight;
    if (containerWidth <= 0 || containerHeight <= 0) return;

    const safeMargin = isFiniteNumber(registration.fitMargin) && registration.fitMargin > 0
      ? registration.fitMargin
      : DEFAULT_FIT_MARGIN;
    const effectiveWidth = rectToFit.w * safeMargin;
    const effectiveHeight = rectToFit.h * safeMargin;
    if (effectiveWidth <= 0 || effectiveHeight <= 0) return;

    const targetZoom = clampZoomForRegistration(
      registration,
      Math.min(containerWidth / effectiveWidth, containerHeight / effectiveHeight)
    );
    const rectCenterX = rectToFit.x + rectToFit.w / 2;
    const rectCenterY = rectToFit.y + rectToFit.h / 2;
    animateKernelViewportTo(registration.normalizedKey, {
      zoomLevel: targetZoom,
      panOffset: {
        x: containerWidth / 2 - rectCenterX * targetZoom,
        y: containerHeight / 2 - rectCenterY * targetZoom,
      },
    }, animationDuration);
  }, [animateKernelViewportTo, resolveRegistration]);

  const fitKernelViewportToRegisteredBounds = useCallback<useViewportsReturnType['fitKernelViewportToRegisteredBounds']>((viewportKey, animationDuration = 240) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration || !isValidBounds(registration.contentBounds)) return;
    fitKernelViewportToRect(registration.normalizedKey, registration.contentBounds, animationDuration);
  }, [fitKernelViewportToRect, resolveRegistration]);

  const zoomKernelViewportIn = useCallback<useViewportsReturnType['zoomKernelViewportIn']>((viewportKey) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration?.containerElement) return;
    const rect = registration.containerElement.getBoundingClientRect();
    zoomViewportAroundPoint(registration.normalizedKey, { x: rect.width / 2, y: rect.height / 2 }, BUTTON_ZOOM_FACTOR, 160);
  }, [resolveRegistration, zoomViewportAroundPoint]);

  const zoomKernelViewportOut = useCallback<useViewportsReturnType['zoomKernelViewportOut']>((viewportKey) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration?.containerElement) return;
    const rect = registration.containerElement.getBoundingClientRect();
    zoomViewportAroundPoint(registration.normalizedKey, { x: rect.width / 2, y: rect.height / 2 }, 1 / BUTTON_ZOOM_FACTOR, 160);
  }, [resolveRegistration, zoomViewportAroundPoint]);

  const resetKernelViewport = useCallback<useViewportsReturnType['resetKernelViewport']>((viewportKey) => {
    const registration = resolveRegistration(viewportKey);
    if (!registration) return;
    animateKernelViewportTo(
      registration.normalizedKey,
      sanitizeViewportForRegistration(registration, registration.defaultViewport),
      180
    );
  }, [animateKernelViewportTo, resolveRegistration]);

  return {
    registerKernelViewport,
    unregisterKernelViewport,
    getKernelViewportState,
    setKernelViewportState,
    applyKernelViewportTransform,
    handleKernelViewportWheel,
    handleKernelViewportPointerDown,
    handleKernelViewportPointerMove,
    handleKernelViewportPointerUp,
    fitKernelViewportToRect,
    fitKernelViewportToRegisteredBounds,
    zoomKernelViewportIn,
    zoomKernelViewportOut,
    resetKernelViewport,
  };
};
